/**
 * List / upload / delete knowledge documents (memory-service RAG).
 */
import { useCallback, useEffect, useState } from 'react';
import { API_BASE, ENDPOINTS } from '../config/api';
import { useAuth } from '../context/AuthContext';
import { extractApiError, parseJsonResponse } from '../utils/httpJson';

export type KnowledgeDocumentStatus = 'uploaded' | 'processing' | 'ready' | 'failed';

export interface KnowledgeDocument {
  id: string;
  org_id: string;
  station_id: string;
  device_id: string | null;
  owner_type: 'station' | 'device';
  filename: string;
  mime_type: string;
  size_bytes: number;
  status: KnowledgeDocumentStatus;
  error: string | null;
  uploaded_by: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

interface UseKnowledgeDocumentsOptions {
  orgId: string;
  stationId: string;
  deviceId?: string | null;
  /** When listing for a device agent, include station-shared docs. */
  includeStationShared?: boolean;
}

export function useKnowledgeDocuments({
  orgId,
  stationId,
  deviceId,
  includeStationShared = false,
}: UseKnowledgeDocumentsOptions) {
  const { token } = useAuth();
  const [items, setItems] = useState<KnowledgeDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!orgId || !stationId) {
      setItems([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {
        org_id: orgId,
        station_id: stationId,
      };
      if (deviceId) params.device_id = deviceId;
      if (includeStationShared) params.include_station_shared = 'true';
      const url = new URL(`${API_BASE}${ENDPOINTS.knowledgeDocuments}`, window.location.origin);
      Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
      const headers: Record<string, string> = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(url.toString(), { headers });
      const data = await parseJsonResponse(res, { url: url.toString() });
      if (!res.ok) {
        throw new Error(extractApiError(data, `Request failed (${res.status})`));
      }
      const list = (data as { items?: KnowledgeDocument[] }).items ?? [];
      setItems(list);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [orgId, stationId, deviceId, includeStationShared, token]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Polling for real-time status updates
  useEffect(() => {
    const hasPending = items.some(
      (doc) => doc.status === 'uploaded' || doc.status === 'processing'
    );
    if (!hasPending) return;

    const intervalId = setInterval(() => {
      void refresh();
    }, 3000);

    return () => clearInterval(intervalId);
  }, [items, refresh]);

  const upload = useCallback(
    async (file: File) => {
      if (!orgId || !stationId) throw new Error('org and station required');
      setUploading(true);
      setError(null);
      try {
        const form = new FormData();
        form.append('org_id', orgId);
        form.append('station_id', stationId);
        form.append('file', file);
        if (deviceId) form.append('device_id', deviceId);
        const url = `${API_BASE}${ENDPOINTS.knowledgeUpload}`;
        const headers: Record<string, string> = {};
        if (token) headers.Authorization = `Bearer ${token}`;
        const res = await fetch(url, { method: 'POST', headers, body: form });
        const data = await parseJsonResponse(res, { url });
        if (!res.ok) {
          throw new Error(extractApiError(data, `Upload failed (${res.status})`));
        }
        await refresh();
        return data as KnowledgeDocument;
      } finally {
        setUploading(false);
      }
    },
    [orgId, stationId, deviceId, token, refresh],
  );

  const remove = useCallback(
    async (documentId: string) => {
      const url = new URL(
        `${API_BASE}${ENDPOINTS.knowledgeDocument(documentId)}`,
        window.location.origin,
      );
      url.searchParams.set('org_id', orgId);
      const headers: Record<string, string> = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(url.toString(), { method: 'DELETE', headers });
      const data = await parseJsonResponse(res, { url: url.toString() });
      if (!res.ok) {
        throw new Error(extractApiError(data, `Delete failed (${res.status})`));
      }
      await refresh();
    },
    [orgId, token, refresh],
  );

  return { items, loading, uploading, error, refresh, upload, remove };
}
